import { getFontFamilyCSS } from '../services/brand-settings';
import { teboxDesignSystem } from '../styles/tebox-booking';
import type { BookingBrandColors } from './booking-brand-colors';

export type BookingModalFormBrandColors = Pick<
  BookingBrandColors,
  | 'textColor'
  | 'secondaryColor'
  | 'borderColor'
  | 'buttonColor'
  | 'primaryColor'
  | 'primaryTextColor'
  | 'buttonTextColor'
  | 'fontFamily'
  | 'borderRadius'
  | 'backgroundColor'
>;

export function buildBookingModalFormStyles(brandColors: BookingModalFormBrandColors) {
  const resolvedFontFamily = getFontFamilyCSS(brandColors.fontFamily || '');
  const surfaceBackground = brandColors.backgroundColor || '#FFFFFF';
  const cardRadius = `var(--brand-border-radius, ${brandColors.borderRadius || teboxDesignSystem.borderRadius.lg})`;
  const controlRadius = brandColors.borderRadius || '6px';
  const hairline = brandColors.borderColor || 'rgba(15, 23, 42, 0.12)';
  const accent = brandColors.buttonColor || brandColors.primaryColor || '#000000';
  const padX = { xs: 3, sm: 4 };
  const padYTop = { xs: 3, sm: 3.5 };
  const padYBottom = { xs: 3, sm: 4 };
  const gapSection = 3;
  const gapFields = 2;
  const gapLabelInput = 0.5;

  const brandCssVars = {
    '--brand-font': resolvedFontFamily,
    '--brand-border-radius': brandColors.borderRadius || teboxDesignSystem.borderRadius.lg,
    '--brand-text': brandColors.textColor,
    '--brand-secondary': brandColors.secondaryColor,
    '--brand-border': hairline,
    '--brand-button': accent,
    '--brand-button-text':
      brandColors.primaryTextColor || brandColors.buttonTextColor || '#FFFFFF',
    '--brand-background': surfaceBackground,
  } as const;

  const muiBrandFontScope = {
    '& .MuiInputBase-root, & .MuiFormControlLabel-label, & .MuiButton-root, & .MuiTypography-root, & .MuiAlert-message':
      {
        fontFamily: resolvedFontFamily,
      },
  };

  const fieldLabelSx = {
    display: 'block' as const,
    fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
    fontWeight: 500,
    lineHeight: 1.3,
    color: brandColors.textColor,
    fontFamily: resolvedFontFamily,
    mb: gapLabelInput,
  };

  const inputSx = {
    fontFamily: resolvedFontFamily,
    '& .MuiInputBase-input': {
      fontSize: teboxDesignSystem.typography.body.fontSize,
      color: brandColors.textColor,
      fontFamily: resolvedFontFamily,
      py: 1.25,
      '&::placeholder': {
        color: brandColors.secondaryColor,
        opacity: 0.85,
      },
    },
    '& .MuiOutlinedInput-root': {
      fontFamily: resolvedFontFamily,
      borderRadius: controlRadius,
      backgroundColor: surfaceBackground,
      transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: hairline },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: `color-mix(in srgb, ${hairline} 55%, ${brandColors.textColor})`,
      },
      '&.Mui-focused': {
        boxShadow: `0 0 0 2px ${accent}28`,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: accent,
        borderWidth: '1px',
      },
    },
  };

  const primaryCtaSx = {
    borderRadius: controlRadius,
    backgroundColor: accent,
    color: brandColors.primaryTextColor || brandColors.buttonTextColor || '#FFFFFF',
    textTransform: 'none' as const,
    fontSize: teboxDesignSystem.typography.body.fontSize,
    fontWeight: 500,
    fontFamily: resolvedFontFamily,
    py: 1.25,
    letterSpacing: '-0.01em',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: accent,
      filter: 'brightness(1.05)',
      boxShadow: `0 10px 28px -8px ${accent}66`,
    },
    '&:disabled': {
      backgroundColor: brandColors.secondaryColor,
      color: brandColors.primaryTextColor || brandColors.buttonTextColor || '#FFFFFF',
      opacity: 0.7,
    },
  };

  const helperTextSx = {
    m: 0,
    fontSize: teboxDesignSystem.typography.body.fontSize,
    fontWeight: 400,
    lineHeight: 1.5,
    color: brandColors.secondaryColor,
    fontFamily: resolvedFontFamily,
  };

  const modalTitleSx = {
    fontSize: teboxDesignSystem.typography.h3.fontSize,
    fontWeight: teboxDesignSystem.typography.h3.fontWeight,
    letterSpacing: teboxDesignSystem.typography.h3.letterSpacing,
    lineHeight: teboxDesignSystem.typography.h3.lineHeight,
    color: brandColors.textColor,
    fontFamily: resolvedFontFamily,
    mb: 0.5,
  };

  const switchLabelSx = {
    mx: 0,
    alignItems: 'center',
    '& .MuiFormControlLabel-label': {
      fontFamily: resolvedFontFamily,
      fontSize: teboxDesignSystem.typography.body.fontSize,
      fontWeight: 500,
      color: brandColors.textColor,
    },
  };

  const errorAlertSx = {
    mb: gapSection,
    borderRadius: controlRadius,
    fontSize: teboxDesignSystem.typography.body.fontSize,
    fontFamily: resolvedFontFamily,
    color: brandColors.textColor,
    bgcolor: 'rgba(220, 38, 38, 0.06)',
    border: '1px solid rgba(220, 38, 38, 0.22)',
  };

  const closeIconButtonSx = {
    flexShrink: 0,
    mt: -0.25,
    color: brandColors.secondaryColor,
    bgcolor: `color-mix(in srgb, ${brandColors.textColor} 4%, ${surfaceBackground})`,
    borderRadius: controlRadius,
    '&:hover': {
      bgcolor: `color-mix(in srgb, ${brandColors.textColor} 8%, ${surfaceBackground})`,
      color: brandColors.textColor,
    },
  };

  const dialogBackdropSx = {
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  };

  const dialogPaperSx = {
    ...brandCssVars,
    ...muiBrandFontScope,
    width: 'min(500px, calc(100vw - 32px))',
    maxWidth: '100%',
    borderRadius: cardRadius,
    border: `1px solid ${hairline}`,
    boxShadow: 'none',
    fontFamily: resolvedFontFamily,
    color: brandColors.textColor,
    overflow: 'hidden',
    bgcolor: surfaceBackground,
  };

  const dialogContentSx = {
    ...muiBrandFontScope,
    px: padX,
    pt: gapSection,
    pb: padYBottom,
    bgcolor: surfaceBackground,
    color: brandColors.textColor,
  };

  const dialogTitleShellSx = {
    m: 0,
    p: 0,
    bgcolor: surfaceBackground,
    color: brandColors.textColor,
  };

  return {
    fontFamily: resolvedFontFamily,
    surfaceBackground,
    cardRadius,
    controlRadius,
    hairline,
    accent,
    padX,
    padYTop,
    padYBottom,
    gapSection,
    gapFields,
    fieldLabelSx,
    inputSx,
    primaryCtaSx,
    helperTextSx,
    modalTitleSx,
    switchLabelSx,
    errorAlertSx,
    closeIconButtonSx,
    dialogBackdropSx,
    dialogPaperSx,
    dialogContentSx,
    dialogTitleShellSx,
  };
}
